import React from 'react';
import DOMPurify from 'dompurify';
import { fmtDate as fmtDateText, fmtDateOnlyText, money as formatMoneyText } from './account/accountFieldFormat';

/**
 * Shared FlowTable cell renderers.
 *
 * The account and contact tables show many of the SAME fields — U5 put the
 * twelve `account_*` columns on the contact table, and both tables have
 * always shown dates and scores — so a field has to READ identically on both
 * or the same number looks like two different numbers depending on which tab
 * a rep is on. These three renderers are the shared vocabulary that
 * guarantees that.
 *
 * They live here, not exported off one of the two table components, so
 * neither table has to import the other's module (a page component is not a
 * utility module — importing one for a formatter drags its whole dependency
 * graph along and invites an import cycle the first time the dependency runs
 * the other way).
 */

/** The one "no value" mark. Never a blank cell — blank reads as a bug. */
export function EmDash(): React.ReactElement {
  return <span className="text-content-faint dark:text-nightshade-600">—</span>;
}

/** ISO timestamp → compact local date; the raw string if it won't parse. The text form is `accountFieldFormat.ts`'s `fmtDate`. */
export function fmtDate(iso: string | null): React.ReactNode {
  return fmtDateText(iso) ?? <EmDash />;
}

/**
 * A CALENDAR DAY → compact local date; the raw string if it won't parse.
 *
 * The date-only twin of `fmtDate`, and not interchangeable with it (round-1
 * fix D-K / R15). `fmtDate` is correct for a timestamptz: it converts a real
 * instant into the reader's own day. A Postgres `DATE` is not an instant —
 * Prisma serialises it as `…T00:00:00.000Z`, so running it through
 * `new Date(iso)` reads UTC midnight and renders the PREVIOUS day for every
 * negative-offset (US) reader. Parse the `YYYY-MM-DD` prefix into a LOCAL
 * `Date` instead, so a calendar day reads identically in every timezone.
 *
 * Use this for a column backed by a Postgres `DATE`; keep `fmtDate` for
 * every `timestamptz`.
 */
export function fmtDateOnly(value: string | null | undefined): React.ReactNode {
  return fmtDateOnlyText(value) ?? <EmDash />;
}

/** A 0-100-ish score: bold, tabular, one decimal only when it has one. */
export function score(v: number | null): React.ReactNode {
  if (v == null) return <EmDash />;
  return <span className="font-semibold tabular-nums">{Number.isInteger(v) ? v : v.toFixed(1)}</span>;
}

/** A dollar amount in the shared `accountFieldFormat` shape, tabular, or the empty glyph. */
export function money(v: number | null): React.ReactNode {
  const text = formatMoneyText(v);
  return text == null ? <EmDash /> : <span className="tabular-nums">{text}</span>;
}

/**
 * External-link glyph appended to every anchor inside sanitized rich-HTML SFDC
 * fields (Engagement Summary, Past Employer (Clay Customer), etc.) — mirrors
 * links.tsx's lucide `ExternalLink` icon usage (see
 * docs/flow/command-ux-bucket-b-prototype.html :155-160 `.esum` styling and
 * :813-819 behavior). Moved here from AccountDetailPanels.tsx (2026-08-26) so
 * ContactFlowTable can share the same sanitize-and-render treatment instead
 * of re-implementing it for `past_employers_clay_customers`.
 */
const RICH_HTML_EXT_GLYPH_CLASS = 'esum-ext-glyph';
const RICH_HTML_EXT_GLYPH =
  `<svg class="${RICH_HTML_EXT_GLYPH_CLASS}" viewBox="0 0 24 24" width="11" height="11" fill="none" ` +
  'stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" ' +
  'style="display:inline-block;flex:none;vertical-align:-1px;margin-left:3px">' +
  '<path d="M15 3h6v6"/><path d="M10 14 21 3"/>' +
  '<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>';

/**
 * DOMPurify `afterSanitizeAttributes` hook: force every `<a>` to open safely
 * in a new tab and append the external-link glyph. Registered immediately
 * before `sanitize()` and unregistered immediately after (see
 * `sanitizeRichHtml` below) so it never leaks to any other
 * `DOMPurify.sanitize()` call site in the app.
 */
function forceExternalAnchor(node: Node): void {
  if (!(node instanceof Element) || node.tagName !== 'A') return;
  node.setAttribute('target', '_blank');
  node.setAttribute('rel', 'noopener noreferrer');
  if (!node.querySelector(`.${RICH_HTML_EXT_GLYPH_CLASS}`)) {
    node.insertAdjacentHTML('beforeend', RICH_HTML_EXT_GLYPH);
  }
}

/**
 * Sanitize a rich-HTML SFDC field (a `<ul>`/`<a>` fragment, e.g.
 * `Engagement_Summary_90_days__c` or `Past_Employers_Clay_Customers__c`) and
 * force every anchor to open in a new tab with `rel="noopener noreferrer"`,
 * appending the ↗ glyph (GTMOPS-2136 / Decision 11's original treatment).
 * The single render site for any read-only rich-HTML field in Flow — pair
 * with `dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(value) }}`.
 */
export function sanitizeRichHtml(html: string): string {
  DOMPurify.addHook('afterSanitizeAttributes', forceExternalAnchor);
  try {
    return DOMPurify.sanitize(html);
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes', forceExternalAnchor);
  }
}
